import { TanStackPostalCodeField } from "@sellar-io/components/adapters";
import { Button, H2 } from "@sellar-io/components";

import { useForm, useStore } from "@tanstack/react-form";
import { useNavigate } from "react-router";
import { useIntercom } from "react-use-intercom";
import { useLocalStorage } from "usehooks-ts";
import { SupplierGetResponseType } from "@sellar-io/api-v2/types";
import { Icon } from "../../../../../../components";
import {
  Dialog,
  DialogContent,
  DialogCTA,
} from "../../../../../../components/modal/Dialog";
import { useEvents } from "../../../../../../hooks";
import poweredBySellarImg from "../../../../../../images/powered-by-sellar.svg";
import { generateLoginLink } from "../../../../../../utils/sellarHelpers";
import { StorefrontSignupDialogSupplierInfo } from "../StorefrontSignupDialog/StorefrontSignupDialogSupplierInfo";

export const StorefrontPostcodeDialog = ({
  active,
  onDismiss,
  supplier,
  setUnAuthorisedUserPostcode,
  title,
  description,
}: {
  active: boolean;
  onDismiss: () => void;
  supplier: SupplierGetResponseType | undefined;
  setUnAuthorisedUserPostcode: (postcode: string) => void;
  title: string;
  description: string;
}) => {
  const navigate = useNavigate();
  const { track } = useEvents();
  const [, setUnAuthorisedUserLocation] = useLocalStorage<
    { lat: number; lng: number } | undefined
  >("unAuthorisedUserLocation", undefined);
  const intercom = useIntercom();

  const form = useForm({
    defaultValues: { postcode: "" },
    onSubmit: async ({ value }) => {
      setUnAuthorisedUserPostcode(value.postcode);
      setUnAuthorisedUserLocation(undefined);
      await track("Postcode Entered");
      onDismiss();
    },
  });
  const postcode = useStore(form.store, (state) => state.values.postcode);

  return (
    <Dialog
      active={active}
      className={"p-4 md:h-4/5 md:w-full md:p-8 lg:w-[480px] xl:max-h-[768px]"}
    >
      <div className="flex w-full items-end justify-end">
        <Icon
          className={"cursor-pointer"}
          onClick={() => intercom.show()}
          icon={"HELP_CIRCLE_OUTLINE"}
        />
      </div>
      <DialogContent className={"m-auto max-h-[768px]"}>
        {supplier && <StorefrontSignupDialogSupplierInfo supplier={supplier} />}
        <div className={"m-auto flex w-full flex-col gap-2 xl:gap-4"}>
          <div className={"flex flex-col gap-2"}>
            <H2 className={"mt-4 px-4 text-center"}>{title}</H2>
            <p className={"text-center"}>{description}</p>
          </div>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <TanStackPostalCodeField
              form={form}
              name="postcode"
              country="GB"
              placeholder="SW1A 1AA"
            />
            <DialogCTA className={"px-0"}>
              <Button
                disabled={!postcode}
                className={"m-0 w-full"}
                type={"submit"}
              >
                Confirm
              </Button>
            </DialogCTA>
            <div className={"p-4 text-center"}>
              <span>
                Already have a Sellar account?{" "}
                <span
                  className={"cursor-pointer underline"}
                  onClick={() => navigate(generateLoginLink())}
                >
                  Log in
                </span>
              </span>
            </div>
          </form>
          <div className="mt-auto flex items-center justify-center">
            <img
              className={"w-[140px]"}
              src={poweredBySellarImg}
              alt={"Powered by Sellar"}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
